import React from 'react';
import { ImgProps } from '@custom-types/ImgProps';
import MuiBox from '@new-components/atoms/MuiBox';
import Image from '@new-components/atoms/Image';
import MuiTypography from '@new-components/atoms/MuiTypography';
import MuiLink from '@new-components/atoms/MuiLink';
import MuiTooltip from '@new-components/atoms/MuiTooltip';
import styles from './styles';
import { ButtonBase, Chip, Divider } from '@mui/material';
import Zoom from '@mui/material/Zoom';
import { useLeadForm } from '@new-components/templates/LeadGeneration/provider';
import { trackMouseClicks } from '@utils/googleAnalytics/googleAnalyticsEvents';


export const EmailIcon = () => {
  return (
    <svg
      width='15'
      height='15'
      viewBox='0 0 20 20'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M1.66699 5.83325L8.47109 10.5961C9.02207 10.9818 9.29756 11.1746 9.59721 11.2493C9.8619 11.3153 10.1387 11.3153 10.4034 11.2493C10.7031 11.1746 10.9786 10.9818 11.5296 10.5961L18.3337 5.83325M5.66699 16.6666H14.3337C15.7338 16.6666 16.4339 16.6666 16.9686 16.3941C17.439 16.1544 17.8215 15.772 18.0612 15.3016C18.3337 14.7668 18.3337 14.0667 18.3337 12.6666V7.33325C18.3337 5.93312 18.3337 5.23306 18.0612 4.69828C17.8215 4.22787 17.439 3.84542 16.9686 3.60574C16.4339 3.33325 15.7338 3.33325 14.3337 3.33325H5.66699C4.26686 3.33325 3.5668 3.33325 3.03202 3.60574C2.56161 3.84542 2.17916 4.22787 1.93948 4.69828C1.66699 5.23306 1.66699 5.93312 1.66699 7.33325V12.6666C1.66699 14.0667 1.66699 14.7668 1.93948 15.3016C2.17916 15.772 2.56161 16.1544 3.03202 16.3941C3.5668 16.6666 4.26686 16.6666 5.66699 16.6666Z'
        stroke='black'
        strokeWidth='1.66667'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};


export interface DestinationCommunityCardProps {
  id: string;
  bgImg: ImgProps;
  name: string;
  description: string;
  status: string;
  apartmentDetailsTitle: string[];
  completionDate: string;
  beds: string;
  size: string;
  sizeTooltip?: string;
  priceLabel: string;
  price: string;
  communityButtonLabel: string;
  propertyListButtonLabel: string;
  communityLink: string;
  propertyListLink: string;
  brandType: 'aldar' | 'lsq';
}

const DestinationCommunityCard: React.FC<DestinationCommunityCardProps> = (
  props
) => {
  const { show } = useLeadForm();
  const handleEnquireClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (props.brandType === 'aldar')
      return show({
        formType: 'COMMUNITY_FORM',
        id: props.id,
      });
    show({
      formType: 'LSQ_COMMUNITY_FORM',
      id: props.id,
    });
    trackMouseClicks({type: 'button', label: 'register_interest'});
  }
  const handlePropertyListClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  }
  return (
    <MuiLink href={props.communityLink} data-testid={'destination-community-card'} sx={styles.container}>
      <MuiBox sx={styles.bgWrapper}>
        <Image
          src={props.bgImg.src}
          data-src={props.bgImg.src}
          alt={props.bgImg.alt}
          fill
          style={{ objectFit: 'cover' }}
        />
      </MuiBox>
      <MuiBox sx={styles.contentWrapper}>
        <MuiBox sx={styles.contentFirstRow}>
          <MuiBox sx={styles.headerWrapper}>
            <MuiTypography data-testid='latest-launch-title' sx={styles.name}>
              {props.name}
            </MuiTypography>
            <MuiTypography
              data-testid='latest-launch-subtitle'
              sx={styles.caption}>
              {props.description}
            </MuiTypography>
            <Chip label={props.status} sx={styles.statusChip} />
          </MuiBox>
          <MuiBox sx={styles.apartmentWrapper}>
            {props.apartmentDetailsTitle.length > 0 && (
              <MuiBox sx={styles.apartmentTitle}>
                {props.apartmentDetailsTitle.map((unitType) => (
                  <Chip key={unitType} label={unitType} sx={styles.unitTypes} title={unitType} />
                ))}
              </MuiBox>
            )}
            <MuiBox sx={styles.apartmentDetailsList}>
              <MuiBox sx={styles.apartmentDetailsItem}>
                <MuiBox sx={styles.apartmentPlaceholderIcon}>
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M11 1.33334V2.66668M3 1.33334V2.66668'
                      stroke='white'
                      strokeWidth='1.25'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M0.666748 8.16216C0.666748 5.25729 0.666748 3.80486 1.50149 2.90243C2.33624 2 3.67974 2 6.36675 2H7.63341C10.3204 2 11.6639 2 12.4987 2.90243C13.3334 3.80486 13.3334 5.25729 13.3334 8.16216V8.5045C13.3334 11.4094 13.3334 12.8618 12.4987 13.7642C11.6639 14.6667 10.3204 14.6667 7.63341 14.6667H6.36675C3.67974 14.6667 2.33624 14.6667 1.50149 13.7642C0.666748 12.8618 0.666748 11.4094 0.666748 8.5045V8.16216Z'
                      stroke='white'
                      strokeWidth='1.25'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M1 5.33334H13'
                      stroke='white'
                      strokeWidth='1.25'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </MuiBox>
                <MuiTypography sx={styles.apartmentLabel}>
                  {props.completionDate}
                </MuiTypography>
              </MuiBox>
              <MuiBox sx={styles.apartmentDetailsSubList}>
                <MuiTooltip
                  title={props.sizeTooltip}
                  slots={{
                    transition: Zoom,
                  }}>
                  <MuiBox sx={styles.apartmentDetailsItemSize}>
                    <MuiBox sx={styles.apartmentPlaceholderIcon}>
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'>
                        <path
                          d='M7.99992 14.6667C4.85722 14.6667 3.28587 14.6667 2.30956 13.6904C1.33325 12.7141 1.33325 11.1427 1.33325 8.00001C1.33325 4.85731 1.33325 3.28597 2.30956 2.30965C3.28587 1.33334 4.85722 1.33334 7.99992 1.33334C11.1426 1.33334 12.714 1.33334 13.6903 2.30965C14.6666 3.28597 14.6666 4.85731 14.6666 8.00001C14.6666 11.1427 14.6666 12.7141 13.6903 13.6904C13.146 14.2347 12.4167 14.4755 11.3333 14.5821'
                          stroke='white'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M8 14.6667C8 12.8257 9.49238 11.3333 11.3333 11.3333'
                          stroke='white'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M1.33325 6H9.33325'
                          stroke='white'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M12 6L14.6667 6'
                          stroke='white'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M6 14.6667L6 6.00002'
                          stroke='white'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M6 3.33334L6 1.33334'
                          stroke='white'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </MuiBox>
                    <MuiTypography sx={styles.apartmentLabel}>
                      {props.size}
                    </MuiTypography>
                  </MuiBox>
                </MuiTooltip>
                <MuiBox sx={styles.apartmentDetailsItem}>
                  <MuiBox sx={styles.apartmentPlaceholderIcon}>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M1.33325 1.33331L1.33325 12.6666'
                        stroke='white'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M14.6665 4.66666L14.6665 12.6667'
                        stroke='white'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M1.33325 4.66666L3.99992 5.99999L14.6666 5.99999'
                        stroke='white'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M1.33325 9.33331L3.99992 9.33331M14.6666 9.33331L12.6666 9.33331'
                        stroke='white'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M4 6L4 10C4 11.1031 4.23022 11.3333 5.33333 11.3333L11.3333 11.3333C12.4364 11.3333 12.6667 11.1031 12.6667 10L12.6667 6'
                        stroke='white'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                      <path
                        d='M4.54233 5.99999C4.59582 5.71729 4.68005 5.42165 4.6637 5.13141C4.63952 4.70239 4.42456 4.31082 4.08419 4.07578C3.94977 3.98296 3.65325 3.88825 3.34171 3.80727C2.88712 3.68912 2.65983 3.63005 2.3942 3.69095C2.20553 3.73421 2.04164 3.83457 1.80735 4.01385C1.58968 4.18041 1.42869 4.40973 1.33325 4.66666V4.66666'
                        stroke='white'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </MuiBox>
                  <MuiTypography sx={styles.apartmentLabel}>
                    {props.beds}
                  </MuiTypography>
                </MuiBox>
              </MuiBox>
            </MuiBox>
          </MuiBox>
        </MuiBox>
        <Divider sx={styles.contentDivider} />
        <MuiBox sx={styles.contentSecondRow}>
          <MuiTypography sx={styles.priceHelper}>
            {props.priceLabel}
          </MuiTypography>
          <MuiTypography sx={styles.price}>{props.price}</MuiTypography>
        </MuiBox>
        <MuiBox sx={styles.contentThirdRow}>
          <MuiBox
            onClick={handleEnquireClick}
            data-testid='community-enquire-button'
            sx={styles.communityDetailsLink}>
            <MuiBox component='span' sx={{ mr: "4px", display: 'flex' }}>

              <EmailIcon />
            </MuiBox>
            ENQUIRE
          </MuiBox>
          {props.propertyListButtonLabel ? (
            <MuiLink
              onClick={handlePropertyListClick}
              href={props.propertyListLink}
              data-testid='latest-launch-register-button'
              sx={styles.propertyListLink}>
              {props.propertyListButtonLabel}
            </MuiLink>
          ) : (
            <MuiBox sx={{ flex: '1 1 auto' }} />
          )}
        </MuiBox>
      </MuiBox>
    </MuiLink>
  );
};

export default DestinationCommunityCard;
